/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import { createJpdContainer } from '@jira/polaris__lib-react-sweet-state-utils/src/createJpdContainer.tsx';

import type {
	Selector,
	SelectorCreator,
} from '@jira/polaris__lib-react-sweet-state-utils/src/SelectorCreator.types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	type BoundActions,
	type ContainerComponent,
	type Store,
} from '@atlassian/react-sweet-state';
import { createDeferredPromise } from '../common/utils.tsx';
import { ConditionalSpotlightManager } from '../ui/ConditionalSpotlightManager.tsx';
import { actions, type Actions } from './actions/index.tsx';
import type { Props, State } from './types.tsx';

export const OnboardingFlowsStore: Store<State, Actions, Props> = createStore<
	State,
	Actions,
	Props
>({
	name: 'PolarisLibOnboardingFlowsStore',
	initialState: {
		containerProps: {
			accountId: undefined,
		},
		productUsageStartTimestamp: undefined,
		globalFields: {
			goToProjectSpotlightDismissed: undefined,
			tourDismissed: undefined,
			tourSpotlightStep: null,
		},
		projectFields: {
			exploreSectionMessageDismissed: undefined,
			seenPulsatingFieldsButton: undefined,
			tourDismissed: undefined,
			tourSpotlightStep: null,
			pageViewed: undefined,
		},
		collections: {
			tourDismissed: undefined,
			tourSpotlightStep: null,
			introductionDismissed: undefined,
			introductionPrerequisiteDismissalsMet: undefined,
		},
		crossProjectView: {},
		import: {
			successNotificationViewed: undefined,
		},
		timeline: {
			sectionMessageDismissed: undefined,
			weeklyModeSpotlightSeen: undefined,
		},
		allViews: {
			spotlightDismissed: undefined,
		},
		prioritizationView: {
			tourDismissed: undefined,
			tourSpotlightStep: null,
		},
		roadmapView: {
			spotlightDismissed: undefined,
			columnConfigSpotlightDismissed: undefined,
		},
		blueprints: {
			tourDismissed: undefined,
		},
		premium: {
			modalDismissed: undefined,
			lastVisit: {
				isPremium: undefined,
				updated: undefined,
			},
			promise: createDeferredPromise(),
		},
		customDates: {
			customizeSpotlightDismissed: undefined,
		},
		unifiedShareDialog: {
			spotlightDismissed: undefined,
		},
		treeViewOnboarding: {
			spotlightDismissed: undefined,
		},
		cardCoverSpotlight: {
			spotlightDismissed: undefined,
		},
		globalViews: {
			sectionMessageDismissed: undefined,
		},
		startWithTemplateSpotlight: {
			spotlightDismissed: undefined,
		},
	},
	actions,
});

const dest = createJpdContainer<Props, State, Actions>(OnboardingFlowsStore);
export const OnboardingFlowsContainerInternal: ContainerComponent<Props> = dest.Container;
export const useOnboardingFlowsActions: () => BoundActions<State, Actions> = dest.useActions;
export const createHook: <TValue>(selector: Selector<State, Props, TValue>) => () => TValue =
	dest.createHook;
export const createHigherLevelHook: <TValue, SelectorCreatorArgs extends ReadonlyArray<unknown>>(
	selectorCreator: SelectorCreator<State, Props, TValue, SelectorCreatorArgs>,
) => (...rest: SelectorCreatorArgs) => TValue = dest.createHigherLevelHook;

export const OnboardingFlowsContainer = ({
	accountId,
	children,
}: React.PropsWithChildren<{
	accountId: Props['accountId'];
}>): React.JSX.Element => (
	<ConditionalSpotlightManager>
		<OnboardingFlowsContainerInternal isGlobal accountId={accountId}>
			{children}
		</OnboardingFlowsContainerInternal>
	</ConditionalSpotlightManager>
);
